import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { mdiChevronLeft, mdiDotsHorizontal } from '@mdi/js'
import Icon from '@mdi/react'
import { memo, useCallback } from 'react'
import { openMenuDialog } from '../../../../actions/menu'
import { closeMessage } from '../../../../actions/message'
import { openRoomState } from '../../../../actions/room'
import { HISTORY } from '../../../../state/state.router'
import { safeObj } from '../../../../utilities/safe'
import { TransitionTypes } from '../../../../utilities/transitions'
import { AppStateType, useAppState } from '../../../Hooks/useAppState'
import { useElementSize } from '../../../Hooks/useElementSize'
import { useModelColor } from '../../../Hooks/useModelColor'
import { useUserSelector } from '../../../Hooks/useUser'
import BadgeAvatar from '../../../common/BadgeAvatar'
import { getThemeSpacing } from '../../../styles/appTheme'
import APP_BREAKPOINTS, {
    maxRoomWidth,
    minRoomWidth,
} from '../../../styles/breakpoints'
import { palette } from '../../../styles/colors'
import { COMPONENT_HEIGHTS } from '../../../theme'

const SHARE_IMAGE_WIDTH = 70

const rootStyles = css({
    position: 'relative',
    padding: getThemeSpacing(0.5),
    borderBottom: `1px solid ${palette.drawerDivider}`, //'solid 1px rgba(228,228,228,1)',
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'space-between',
    background: palette.backgroundRaised,
    height: COMPONENT_HEIGHTS.chatHeaderOffline,
    minHeight: COMPONENT_HEIGHTS.chatHeaderOffline,
    [minRoomWidth(APP_BREAKPOINTS.md)]: {
        height: COMPONENT_HEIGHTS.chatHeaderOfflineDesktop,
        minHeight: COMPONENT_HEIGHTS.chatHeaderOfflineDesktop,
    },
})
const avatarContainerStyles = css({
    marginBottom: getThemeSpacing(1),
    marginTop: getThemeSpacing(0.5),
    position: 'relative' as const,
    [maxRoomWidth(APP_BREAKPOINTS.md)]: {
        marginBottom: 0,
        marginTop: -4,
        transform: 'scale(0.75)',
    },
})
const infoContainerStyles = css({
    display: 'flex',
    flexFlow: 'column nowrap',
    alignItems: 'center',
    marginBottom: getThemeSpacing(0.5),
    zIndex: 1,
    [maxRoomWidth(APP_BREAKPOINTS.md)]: {
        marginBottom: 4,
    },
})
const nameStyles = css({
    margin: 0,
    fontSize: '1em',
    fontWeight: 600,
})

const StyledSubText = styled('span')({
    margin: 0,
    fontSize: '.6em',
    fontWeight: 500,
})
const titleStyles = css({
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'space-between',
    [maxRoomWidth(APP_BREAKPOINTS.md)]: {
        marginTop: -4,
    },
})
const clickStyles = css({
    cursor: 'pointer',
})
const shareContentStyles = css({
    position: 'absolute',
    right: 10,
    bottom: 10,
})
const linkShareWrapperStyles = css({
    display: 'flex',
    flex: `1 1 100%`,
    justifyContent: 'flex-end',
    marginLeft: 0,
})
const shareImageStyles = css({
    width: SHARE_IMAGE_WIDTH,
    background: 'transparent',
    filter: palette.imagesMonochromeFilter,
})

interface OfflineChatHeaderProps {
    id: number
    isProfile?: boolean
    pinned?: boolean
    preview?: boolean
    showBadge?: boolean
    authenticated?: boolean
    isMobile?: boolean
    isIOS?: boolean
    pinnedPm?: boolean
}

const OfflineChatHeader = memo<OfflineChatHeaderProps>((props) => {
    const { id } = props
    const { ref, width } = useElementSize()
    const {
        isMobile = props.isMobile,
        isIOS = props.isIOS,
        isIpad,
    } = useAppState(AppStateType.APP)
    const name = useUserSelector(id, 'name')
    const color = useUserSelector(id, 'color')
    const isBroadcasting = useUserSelector(id, 'isBroadcasting')
    const isOffline = useUserSelector(id, 'isOffline')
    const slug = useUserSelector(id, 'slug')
    const shareAlbums = useUserSelector(id, 'shareAlbums') ?? 0
    const shareThings = useUserSelector(id, 'shareThings') ?? 0
    const shareTipmenus = useUserSelector(id, 'shareTipmenus') ?? 0
    const shareRecordings = useUserSelector(id, 'shareRecordings') ?? 0

    const {
        pinned = false,
        preview = false,
        showBadge = false,
        pinnedPm = false,
        isProfile = false,
    } = safeObj(props)
    const isMobileDevice = isMobile || isIOS || isIpad
    const isPinnedMobile =
        (isMobileDevice || width < APP_BREAKPOINTS.s) && pinned
    const modelColor = useModelColor(color)

    const hasAlbums = shareAlbums > 0
    const hasThings = shareThings > 0
    const hasTipMenus = shareTipmenus > 0
    const hasRecordings = shareRecordings > 0
    const hasShareContent =
        hasAlbums || hasRecordings || hasThings || hasTipMenus

    const showShareButton =
        hasShareContent &&
        !preview &&
        !pinnedPm &&
        !isPinnedMobile &&
        !isProfile &&
        (isMobileDevice ||
            (width > APP_BREAKPOINTS.s && !pinned) ||
            (pinned && width > APP_BREAKPOINTS.s + 30))

    const onBack = useCallback(() => {
        // const { previousURL: url } = NAVSTATE
        const [{ visibleRoute: url }] = HISTORY.lastRoute
        if (url && url.startsWith('/room')) {
            const [, , slug] = url.split('/')

            if (slug) {
                openRoomState(slug)
            }
        }

        closeMessage({ back: true })
    }, [])

    const onOpenMenu = useCallback(() => {
        openMenuDialog(slug, {
            message: true,
            transitionSource: TransitionTypes.ROOM,
        })
    }, [slug])

    if (!id) return null

    return (
        <div
            id={`chat-header-${id}`}
            className="chat-header"
            ref={ref}
            css={rootStyles}
            data-testid="Container__OfflineChatHeader"
        >
            <span onClick={onBack} css={clickStyles} data-testid="Button__Back">
                <Icon size={'2.5em'} path={mdiChevronLeft} />
            </span>
            <div css={infoContainerStyles}>
                <div css={avatarContainerStyles} onClick={onOpenMenu}>
                    <BadgeAvatar
                        disableBadge={!showBadge}
                        broadcasting={isBroadcasting}
                        offline={isOffline}
                        alt={name}
                        size={40}
                        id={id}
                    />
                </div>
                <div css={titleStyles}>
                    <h2 style={{ color: modelColor }} css={nameStyles}>
                        {name}
                    </h2>
                </div>
                {isOffline && (
                    <div css={titleStyles}>
                        <StyledSubText>is offline</StyledSubText>
                    </div>
                )}
            </div>
            {showShareButton && (
                <div css={shareContentStyles}>
                    <div css={linkShareWrapperStyles}>
                        <a
                            href={`https://share.myfreecams.com/${slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="Link__MFCShare"
                        >
                            <img
                                css={shareImageStyles}
                                data-testid="Image__MFCShare"
                                src="https://img.mfcimg.com/files/images/misc/MFCShare.svg"
                            />
                        </a>
                    </div>
                </div>
            )}
            <span onClick={onOpenMenu} css={clickStyles}>
                <Icon size={'2.5em'} path={mdiDotsHorizontal} />
            </span>
        </div>
    )
})

export default OfflineChatHeader
